import { useEffect, useState } from "react";
import api, { formatPrice } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Package, MessageSquare, ShoppingBag, DollarSign, Wrench, Factory } from "lucide-react";

const Stat = ({ icon: Icon, label, value, tone = "primary" }) => (
  <div className="rounded-xl border border-white/10 bg-white/5 backdrop-blur p-5">
    <div className="flex items-center gap-2">
      <Icon className={`w-4 h-4 ${tone === "primary" ? "text-[#0055FF]" : "text-emerald-400"}`} />
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{label}</div>
    </div>
    <div className="mt-2 font-display text-3xl font-extrabold text-white">{value}</div>
  </div>
);

export default function OwnerDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setData(data)); }, []);
  if (!data) return <Skeleton className="h-96 bg-white/5" />;
  const t = data.totals;
  return (
    <div data-testid="owner-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-slate-400">Overview</div>
          <h1 className="mt-1 font-display text-3xl font-extrabold text-white">Dashboard</h1>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <Stat icon={Package} label="Total products" value={t.products} />
        <Stat icon={Factory} label="Active" value={t.active_products} />
        <Stat icon={Factory} label="eBay-linked" value={t.ebay_linked} />
        <Stat icon={ShoppingBag} label="Orders" value={t.orders} />
        <Stat icon={ShoppingBag} label="Paid orders" value={t.paid_orders} tone="success" />
        <Stat icon={DollarSign} label="Revenue" value={formatPrice(t.revenue)} tone="success" />
        <Stat icon={MessageSquare} label="Contact inquiries" value={t.contact} />
        <Stat icon={Wrench} label="Fitment requests" value={t.fitment_requests} />
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-4">
        <section className="rounded-xl border border-white/10 bg-white/5 p-5">
          <h3 className="font-heading font-semibold text-white mb-4">Recent orders</h3>
          <div className="space-y-2">
            {(data.recent_orders || []).length === 0 && <div className="text-sm text-slate-500">No orders yet.</div>}
            {data.recent_orders?.map((o) => (
              <div key={o.order_id} className="flex items-center justify-between p-3 rounded-md bg-white/5 border border-white/10">
                <div>
                  <div className="text-sm text-white font-mono">{o.order_id}</div>
                  <div className="text-xs text-slate-400">{o.email}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-white">{formatPrice(o.total)}</div>
                  <div className={`text-[10px] font-mono uppercase tracking-widest ${o.payment_status === "paid" ? "text-emerald-400" : "text-slate-400"}`}>{o.payment_status}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
        <section className="rounded-xl border border-white/10 bg-white/5 p-5">
          <h3 className="font-heading font-semibold text-white mb-4">Recent fitment requests</h3>
          <div className="space-y-2">
            {(data.recent_fitment || []).length === 0 && <div className="text-sm text-slate-500">No requests yet.</div>}
            {data.recent_fitment?.map((f) => (
              <div key={f.request_id} className="p-3 rounded-md bg-white/5 border border-white/10">
                <div className="text-sm text-white">{f.name}</div>
                <div className="text-xs text-slate-400">{f.year} {f.make} {f.model} · {f.email}</div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
